/**
 * Building Costs Module - Pure calculation functions for building costs
 *
 * This module provides a single source of truth for calculating building costs.
 * All cost logic is centralized here to avoid duplication.
 */

import { BALANCE_CONFIG } from '../../data/balance/config';

/**
 * Calculate base building cost with standard scaling
 * Formula: baseCost × costMultiplier × costScale^owned
 *
 * @param {Object} baseCost - Base cost object { wood: 10, stone: 5 }
 * @param {number} owned - Number of buildings already owned
 * @returns {Object} Scaled cost object
 */
export function calculateBuildingCost(baseCost, owned = 0) {
  if (!baseCost) return {};

  const result = {};
  const scale = BALANCE_CONFIG.buildings.costScale;
  const multiplier = BALANCE_CONFIG.buildings.costMultiplier || 1.0;

  for (const [resource, amount] of Object.entries(baseCost)) {
    result[resource] = Math.floor(amount * multiplier * Math.pow(scale, owned));
  }

  return result;
}

/**
 * Calculate panel building cost with advisor modifiers
 * Formula: calculateBuildingCost() × (1 + advisorBuildingCostMod)
 *
 * @param {Object} building - Building data object with baseCost
 * @param {number} owned - Number of buildings already owned
 * @param {Object} advisorSpecials - Advisor special abilities { buildingCost: -0.2 }
 * @returns {Object} Final cost object
 */
export function calculatePanelBuildingCost(building, owned = 0, advisorSpecials = {}) {
  if (!building || !building.baseCost) return {};

  const baseCost = calculateBuildingCost(building.baseCost, owned);

  // Apply building cost modifier (negative = cheaper)
  const buildingCostMod = advisorSpecials.buildingCost || 0;
  if (buildingCostMod !== 0) {
    const multiplier = 1 + buildingCostMod; // e.g., -0.20 becomes 0.80
    const adjusted = {};
    Object.entries(baseCost).forEach(([res, amt]) => {
      adjusted[res] = Math.max(1, Math.floor(amt * multiplier));
    });
    return adjusted;
  }

  return baseCost;
}

/**
 * Calculate tile building cost (static 2× base, no scaling)
 *
 * @param {Object} building - Building data object with baseCost
 * @returns {Object|null} Cost object or null if invalid
 */
export function calculateTileBuildingCost(building) {
  if (!building || !building.baseCost) return null;

  const cost = {};
  Object.entries(building.baseCost).forEach(([res, amt]) => {
    cost[res] = amt * 2;
  });
  return cost;
}
